import { inject, Injectable } from '@angular/core';
import {
  BackendService,
  BaseApiService,
  RequestFacadeModel,
  RequestModel,
  RequestType,
} from '@cf/core';
import { TranslateService } from '@tolgee/ngx';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { StatusResponse } from '../auth';
import { ICalendar } from './interfaces';
import { CalendarsQuery, CalendarsStore, capitalize } from './store';

@Injectable({
  providedIn: 'root',
})
export class CalendarStoreService extends BaseApiService {
  calendars$: Observable<ICalendar[]> = this.calendarsQuery.calendars$;
  translateService = inject(TranslateService);

  constructor(
    public override backendService: BackendService,
    private readonly calendarsQuery: CalendarsQuery,
    private readonly calendarsStore: CalendarsStore,
  ) {
    super(backendService, 'calendars');
  }

  get calendars(): ICalendar[] {
    return this.calendarsQuery.getValue().data;
  }

  get firstCalendar(): ICalendar | undefined {
    return this.calendars.find(Boolean);
  }

  public getCalendars(): void {
    this.getCalendars$().subscribe();
  }

  public deleteCalendar(id: number): Observable<StatusResponse> {
    const request: RequestModel = new RequestModel({
      url: this.getFullUrl(''),
      successMessage: {
        message: this.translateService.instant('calendars_notifications.disconnected'),
        title: this.translateService.instant('calendars_notifications.title'),
      },
    }).withID(id);
    const requestFacade: RequestFacadeModel = new RequestFacadeModel({
      requestType: RequestType.delete,
      request,
    });
    return this.send<any, any>(requestFacade).pipe(finalize(() => this.getCalendars()));
  }

  private getCalendars$(): Observable<ICalendar[]> {
    const request: RequestModel = new RequestModel({
      url: this.getFullUrl(),
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel({
      requestType: RequestType.get,
      request,
    });
    return this.send<ICalendar[]>(requestFacade).pipe(
      tap((calendars: ICalendar[]) => {
        this.calendarsStore.update({
          data: calendars.map((c) => ({
            ...c,
            specified_email: `${c.email} (${capitalize(c.app_name)})`,
          })),
        });
      }),
    );
  }

  reset(): void {
    this.calendarsStore.reset();
  }
}
